// client/src/coms/components/sidebar_parts/SidebarChannelSections.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimationStage, VoiceAnimationStyles } from '../voiceAnimations';
import { StatusTag } from '../../presence/PresenceUi';
import { statusFor, usePresence } from '../../presence/presenceClient';
import { usePersonalisation } from '../../personalisation/PersonalisationProvider';
import { moveItem, stepItem } from '../../organisation/channelLayout';

const MIC_ICON_SRC = '/mic.png';
const VOICE_ANIMATION_FADE_OUT_MS = 500;

// Touch screens: holding a channel opens the same channel menu as a right-click (browsers that fire
// contextmenu on long-press simply open it once more at the same place). Moving cancels; the tap that ends
// a long-press does not also select the channel.
const longPress = { timer: null, fired: false, x: 0, y: 0 };
function channelLongPress(c, open) {
  if (typeof open !== 'function') return {};
  const clear = () => {
    if (longPress.timer) clearTimeout(longPress.timer);
    longPress.timer = null;
  };
  return {
    onTouchStart(e) {
      const t = e.touches && e.touches[0];
      if (!t || e.touches.length > 1) return;
      clear();
      longPress.fired = false;
      longPress.x = t.clientX;
      longPress.y = t.clientY;
      longPress.timer = setTimeout(() => {
        longPress.timer = null;
        longPress.fired = true;
        open({ preventDefault() {}, stopPropagation() {}, clientX: longPress.x, clientY: longPress.y }, c);
      }, 550);
    },
    onTouchMove(e) {
      const t = e.touches && e.touches[0];
      if (t && Math.hypot(t.clientX - longPress.x, t.clientY - longPress.y) > 10) clear();
    },
    onTouchEnd(e) {
      clear();
      if (longPress.fired) {
        longPress.fired = false;
        if (e.cancelable) e.preventDefault();
      }
    },
    onTouchCancel: clear,
  };
}

function WhiteIconImage({ src, size = 14 }) {
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        display: 'block',
        objectFit: 'contain',
        filter: 'brightness(0) invert(1)',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
      draggable={false}
    />
  );
}

function VoiceMicIcon({ size = 14 }) {
  return <WhiteIconImage src={MIC_ICON_SRC} size={size} />;
}

function VoiceEmojiIcon({ emoji, size = 15 }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'block',
        fontSize: size,
        lineHeight: 1,
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      {emoji}
    </span>
  );
}

function VoiceStatusIcon({ title, children }) {
  return (
    <span
      title={title}
      aria-label={title}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 14,
        height: 14,
        flexShrink: 0,
        opacity: 0.98,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{children}</span>
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 0,
          top: '50%',
          width: '100%',
          height: 2,
          borderRadius: 999,
          background: '#ff4d4f',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.28)',
          transform: 'translateY(-50%) rotate(-35deg)',
          transformOrigin: 'center',
          pointerEvents: 'none',
        }}
      />
    </span>
  );
}

function getSidebarAnimationMetrics(type) {
  const key = String(type || 'flash').trim().toLowerCase();

  const map = {
    bars: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.42,
      offsetX: 0,
      offsetY: 0,
    },
    dots: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.42,
      offsetX: 0,
      offsetY: 0,
    },
    spin: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.42,
      offsetX: 0,
      offsetY: 0,
    },
    breath: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.35,
      offsetX: 0,
      offsetY: 0,
    },
    flash: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.30,
      offsetX: 0,
      offsetY: 0,
    },
    orbit: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.42,
      offsetX: 0,
      offsetY: 0,
    },
    beam: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.42,
      offsetX: 0,
      offsetY: 0,
    },
    matrix: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.42,
      offsetX: 0,
      offsetY: 0,
    },
    droplets: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.42,
      offsetX: 0,
      offsetY: 0,
    },
    blackhole: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.50,
      offsetX: 0,
      offsetY: 0,
    },
    uiversebars: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.80,
      offsetX: 0,
      offsetY: 0,
    },
    dotspinner: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.42,
      offsetX: 0,
      offsetY: 0,
    },
    wavemenu: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.80,
      offsetX: 0,
      offsetY: 0,
    },
    cornerspin: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.42,
      offsetX: 0,
      offsetY: 0,
    },
    ringpulse: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.60,
      offsetX: 0,
      offsetY: 0,
    },
    jumpballs: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.35,
      offsetX: 0,
      offsetY: 0,
    },
    eightball: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.42,
      offsetX: 0,
      offsetY: 0,
    },
    dicegrid: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.42,
      offsetX: 0,
      offsetY: 0,
    },
    auroraring: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.42,
      offsetX: 0,
      offsetY: 0,
    },
    glassripples: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.60,
      offsetX: 0,
      offsetY: 0,
    },
    zigzagballs: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.50,
      offsetX: 0,
      offsetY: 0,
    },
    pacmansolo: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.42,
      offsetX: 0,
      offsetY: 0,
    },
    jumpdots: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 30,
      scale: 0.42,
      offsetX: 0,
      offsetY: 0,
    },
    blurhalo: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.30,
      offsetX: 0,
      offsetY: 0,
    },
    triobits: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.42,
      offsetX: 0,
      offsetY: 0,
    },
    hamsterwheel: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.42,
      offsetX: 0,
      offsetY: 0,
    },
    neonglow: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.42,
      offsetX: 0,
      offsetY: 0,
    },
    radarsweep: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.42,
      offsetX: 0,
      offsetY: 0,
    },
    pixelghost: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.42,
      offsetX: -15,
      offsetY: 0,
    },
    centerlineeq: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.60,
      offsetX: 0,
      offsetY: 0,
    },
    redthreebars: {
      slotWidth: 28,
      slotHeight: 18,
      demoWidth: 42,
      demoHeight: 42,
      scale: 0.62,
      offsetX: 0,
      offsetY: 0,
    },
  };

  return map[key] || {
    slotWidth: 28,
    slotHeight: 18,
    demoWidth: 42,
    demoHeight: 42,
    scale: 0.42,
    offsetX: 0,
    offsetY: 0,
  };
}

function VoiceAnimationSlot({ type, active }) {
  const metrics = getSidebarAnimationMetrics(type);
  const { speakingStill } = usePersonalisation();
  const [visible, setVisible] = useState(!!active);
  const [fadingOut, setFadingOut] = useState(false);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    if (active) {
      setVisible(true);
      setFadingOut(false);
      return undefined;
    }

    if (visible) {
      setFadingOut(true);
      hideTimerRef.current = setTimeout(() => {
        setVisible(false);
        setFadingOut(false);
        hideTimerRef.current = null;
      }, VOICE_ANIMATION_FADE_OUT_MS);
    }

    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [active, visible]);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, []);

  if (!visible) return null;

  return (
    <span
      data-testid="voice-animation"
      data-animation={String(type || 'flash')}
      data-active={active ? '1' : '0'}
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: metrics.slotWidth,
        height: metrics.slotHeight,
        minWidth: metrics.slotWidth,
        maxWidth: metrics.slotWidth,
        minHeight: metrics.slotHeight,
        maxHeight: metrics.slotHeight,
        overflow: 'hidden',
        flexShrink: 0,
        marginLeft: 6,
        verticalAlign: 'middle',
        opacity: active ? 1 : 0,
        transition: `opacity ${VOICE_ANIMATION_FADE_OUT_MS}ms linear`,
        pointerEvents: 'none',
      }}
      className="vx-anim-plate"
    >
      <AnimationStage
        type={String(type || 'flash').trim() || 'flash'}
        still={speakingStill}
        style={{
          width: metrics.demoWidth,
          height: metrics.demoHeight,
          minWidth: metrics.demoWidth,
          minHeight: metrics.demoHeight,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: `translate(${metrics.offsetX}px, ${metrics.offsetY}px) scale(${metrics.scale})`,
          transformOrigin: 'center center',
          pointerEvents: 'none',
          opacity: fadingOut ? 0.999 : 1,
        }}
      />
    </span>
  );
}

function VoiceMemberRow({ row, onMemberContextMenu }) {
  const presence = usePresence();
  const pres = statusFor(presence, Number(row && row.id) || 0, 0);
  const avatarFallback = String(row.shownName || '?').trim().charAt(0).toUpperCase() || '?';
  const animationType =
    String(
      row.voiceAnimation ||
        row.voice_animation ||
        row.active_voice_animation ||
        row.member?.voiceAnimation ||
        row.member?.voice_animation ||
        row.member?.active_voice_animation ||
        'flash'
    ).trim() || 'flash';

  return (
    <div
      className="coms-voice-member"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 10px 4px 26px',
        opacity: row.connected ? 1 : 0.5,
        fontSize: 13,
        userSelect: 'none',
        minWidth: 0,
      }}
      onContextMenu={(e) => onMemberContextMenu(e, row.member)}
      title="Right click for volume"
    >
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: row.connected ? '#4ade80' : '#64748b',
          boxShadow: row.connected ? '0 0 0 2px rgba(74,222,128,0.18)' : 'none',
          flexShrink: 0,
        }}
      />

      <div
        style={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          overflow: 'hidden',
          background: 'var(--vxs-1, #111214)',
          border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.8 * var(--vx-ov, 1)))',
          fontSize: 9,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        {row.rowAvatarUrl ? (
          <img
            src={row.rowAvatarUrl}
            alt={row.shownName}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={(e) => {
              try {
                e.currentTarget.style.display = 'none';
              } catch {}
            }}
          />
        ) : (
          <span>{avatarFallback}</span>
        )}
      </div>

      <div
        className="coms-voice-member-row"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          minWidth: 0,
          flex: 1,
        }}
      >
        <div
          className="coms-voice-member-name"
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
            flex: '0 1 auto',
          }}
        >
          {row.shownName}
        </div>

        <StatusTag status={pres.status} />

        <VoiceAnimationSlot type={animationType} active={!!row.isSpeaking} />

        {row.muted ? (
          <VoiceStatusIcon title="Mic muted">
            <VoiceMicIcon size={14} />
          </VoiceStatusIcon>
        ) : null}

        {row.deafened ? (
          <VoiceStatusIcon title="Speakers muted">
            <VoiceEmojiIcon emoji="🎧" size={15} />
          </VoiceStatusIcon>
        ) : null}
      </div>
    </div>
  );
}

// One list in the server's own order (organisation/channelLayout.js): text and voice channels, categories and
// dividers, in whatever order the server's managers chose. Without a layout (still loading) channels show in
// list order. Managers can drag any item, use Alt+Arrow keys, or the channel menu's Move up / Move down.
export function SidebarChannelSections({
  isOwner,
  textChannels,
  voiceChannels,
  activeChannelId,
  onSelectChannel,
  onSelectVoiceChannel,
  onChannelContextMenu,
  onCreateChannel,
  isMobileViewport,
  joinVoiceFromChannel,
  voiceMembersForChannel,
  renderVoiceMemberRow,
  onMemberContextMenu,
  channelReadStateById,
  archivedChannels = [],
  layout = null,
  onSaveLayout = null,
  onAddLayoutItem = null,
  onOrganise = null,
  isFiltering = false,
}) {
  const dragKey = useRef(null);
  const rootRef = useRef(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('vox:sidebar:collapsedCategories') || '{}') || {};
    } catch {
      return {};
    }
  });
  const toggleCollapsed = (id) =>
    setCollapsed((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try {
        localStorage.setItem('vox:sidebar:collapsedCategories', JSON.stringify(next));
      } catch {}
      return next;
    });
  useEffect(() => {
    const on = (e) => e && e.detail && typeof e.detail === 'object' && setCollapsed(e.detail);
    window.addEventListener('vox-categories-collapsed', on);
    return () => window.removeEventListener('vox-categories-collapsed', on);
  }, []);

  // The layout's items joined to the channel objects this member can see.
  const tree = useMemo(() => {
    const byKey = new Map();
    for (const c of [...(textChannels || []), ...(voiceChannels || [])]) byKey.set(`${c.kind === 'voice' ? 'voice' : 'text'}:${toInt(c.db_id != null ? c.db_id : c.id, 0)}`, c);
    const used = new Set();
    const join = (it) => {
      if (it.type === 'text' || it.type === 'voice') {
        const k = `${it.type}:${toInt(it.id, 0)}`;
        const channel = byKey.get(k);
        if (!channel || used.has(k)) return null;
        used.add(k);
        return { type: it.type, id: toInt(it.id, 0), channel };
      }
      if (it.type === 'category') {
        const children = (it.children || []).map(join).filter(Boolean);
        if (isFiltering && !children.some((c) => c.type !== 'divider')) return null;
        return { type: 'category', id: toInt(it.id, 0), name: String(it.name || ''), children: isFiltering ? children.filter((c) => c.type !== 'divider') : children };
      }
      if (it.type === 'divider') return isFiltering ? null : { type: 'divider', id: toInt(it.id, 0), name: String(it.name || '') };
      return null;
    };
    const out = Array.isArray(layout) ? layout.map(join).filter(Boolean) : [];
    // Channels the layout does not list yet (just created, or the layout is still loading) go last.
    for (const [k, channel] of byKey) {
      if (used.has(k)) continue;
      out.push({ type: channel.kind === 'voice' ? 'voice' : 'text', id: toInt(channel.db_id != null ? channel.db_id : channel.id, 0), channel });
    }
    return out;
  }, [layout, textChannels, voiceChannels, isFiltering]);

  const canArrange = !!onSaveLayout && !isFiltering;
  const collapsedKeys = useMemo(() => new Set(Object.keys(collapsed).filter((id) => collapsed[id]).map((id) => `category:${id}`)), [collapsed]);

  function commit(next, focusKey) {
    setDropTarget(null);
    dragKey.current = null;
    if (!next || !onSaveLayout) return;
    onSaveLayout(next);
    if (focusKey) {
      setTimeout(() => {
        const el = rootRef.current && rootRef.current.querySelector(`[data-layout-key="${focusKey}"]`);
        if (el) el.focus();
      }, 0);
    }
  }

  const arrangeProps = (key, dropMode = 'before') =>
    canArrange
      ? {
          'data-layout-key': key,
          draggable: true,
          onDragStart(e) {
            dragKey.current = key;
            try {
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', key);
            } catch {}
          },
          onDragEnd() {
            dragKey.current = null;
            setDropTarget(null);
          },
          onDragOver(e) {
            if (!dragKey.current || dragKey.current === key) return;
            e.preventDefault();
            const t = `${dropMode}:${key}`;
            if (dropTarget !== t) setDropTarget(t);
          },
          onDrop(e) {
            if (!dragKey.current) return;
            e.preventDefault();
            commit(moveItem(tree, dragKey.current, dropMode === 'into' ? { into: key } : { before: key }));
          },
          onKeyDown(e) {
            if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
            e.preventDefault();
            commit(stepItem(tree, key, e.key === 'ArrowUp' ? -1 : 1, collapsedKeys), key);
          },
          'aria-keyshortcuts': 'Alt+ArrowUp Alt+ArrowDown',
        }
      : { 'data-layout-key': key, draggable: false };
  const dropClass = (key, mode = 'before') => (dropTarget === `${mode}:${key}` ? (mode === 'into' ? ' is-drop-into' : ' is-drop-target') : '');

  function renderTextChannel(c, key) {
    const readState = channelReadStateById && channelReadStateById[c.id] ? channelReadStateById[c.id] : null;
    const hasLoadedNotificationState = !!readState && Object.prototype.hasOwnProperty.call(readState, 'show_notifications');
    const notificationsOn = hasLoadedNotificationState ? toInt(readState && readState.show_notifications, 1) === 1 : true;
    const hasUnread = (!activeChannelId || activeChannelId !== c.id) && notificationsOn && toInt(readState && readState.unread_count, 0) > 0;
    const arrange = arrangeProps(key);

    return (
      <button
        key={key}
        type="button"
        className={`coms-chan ${activeChannelId === c.id ? 'active' : ''}${dropClass(key)}`}
        data-channel-id={c.id}
        {...arrange}
        title={c.topic ? `${c.topic}\n\nCtrl+click or middle-click to open in a new tab` : 'Ctrl+click or middle-click to open in a new tab'}
        onClick={() => onSelectChannel(c.id)}
        onContextMenu={(e) => onChannelContextMenu(e, c)}
        {...channelLongPress(c, onChannelContextMenu)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          opacity: hasLoadedNotificationState && !notificationsOn ? 0.72 : 1,
        }}
      >
        <span
          style={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'left',
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 7,
          }}
        >
          <span
            style={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'left',
            }}
          >
            # {c.name}
          </span>

          {hasUnread ? (
            <span
              data-testid="channel-unread-dot"
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: '#ff3b30',
                flexShrink: 0,
              }}
            />
          ) : null}
        </span>

        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
            minWidth: 14,
            justifyContent: 'flex-end',
          }}
        >
          {hasLoadedNotificationState && !notificationsOn ? (
            <span
              aria-label="Notifications disabled"
              title="Notifications disabled"
              style={{
                fontSize: 13,
                lineHeight: 1,
                opacity: 0.55,
                filter: 'grayscale(1) saturate(0)',
              }}
            >
              🔕
            </span>
          ) : null}
        </span>
      </button>
    );
  }

  function renderVoiceChannel(c, key, { membersOnly = false } = {}) {
    const members = voiceMembersForChannel(c.db_id != null ? c.db_id : c.id);
    const hasMembers = members.connected.length > 0 || members.others.length > 0;
    if (membersOnly && !hasMembers && activeChannelId !== c.id) return null;

    return (
      <div key={key} style={{ width: '100%' }}>
        <button
          type="button"
          className={`coms-chan voice ${activeChannelId === c.id ? 'active' : ''}${dropClass(key)}`}
          data-channel-id={c.id}
          {...arrangeProps(key)}
          onClick={() => {
            if (typeof onSelectVoiceChannel === 'function') onSelectVoiceChannel(c.id);
            else if (typeof onSelectChannel === 'function') onSelectChannel(c.id);

            if (isMobileViewport) {
              joinVoiceFromChannel(c);
            }
          }}
          onDoubleClick={() => {
            if (typeof onSelectVoiceChannel === 'function') onSelectVoiceChannel(c.id);
            else if (typeof onSelectChannel === 'function') onSelectChannel(c.id);

            if (!isMobileViewport) {
              joinVoiceFromChannel(c);
            }
          }}
          onContextMenu={(e) => onChannelContextMenu(e, c)}
          {...channelLongPress(c, onChannelContextMenu)}
          title={isMobileViewport ? 'Tap to join' : 'Double click to join'}
        >
          🔊 {c.name}
        </button>

        {hasMembers ? (
          <div style={{ paddingBottom: 6 }}>
            {members.connected.map((m) => (
              <VoiceMemberRow key={`${m.id}_c`} row={renderVoiceMemberRow(m, true)} onMemberContextMenu={onMemberContextMenu} />
            ))}
            {members.others.map((m) => (
              <VoiceMemberRow key={`${m.id}_o`} row={renderVoiceMemberRow(m, false)} onMemberContextMenu={onMemberContextMenu} />
            ))}
          </div>
        ) : (
          <div
            style={{
              padding: '4px 10px 8px 26px',
              opacity: 0.45,
              fontSize: 12,
              userSelect: 'none',
            }}
          >
            No members
          </div>
        )}
      </div>
    );
  }

  function renderDivider(d, key, { inline = false } = {}) {
    return (
      <div
        key={key}
        className={`vxo-divider${d.name ? ' has-label' : ''}${inline ? ' is-inline' : ''}${dropClass(key)}`}
        role="separator"
        aria-label={d.name ? `Divider: ${d.name}` : 'Divider'}
        tabIndex={canArrange ? 0 : undefined}
        {...arrangeProps(key)}
      >
        {d.name ? <span className="vxo-divider-label coms-sidebar-section-title">{d.name}</span> : null}
      </div>
    );
  }

  function renderChannel(it, opts) {
    const key = `${it.type}:${it.id}`;
    return it.type === 'voice' ? renderVoiceChannel(it.channel, key, opts) : renderTextChannel(it.channel, key);
  }

  function renderCategory(cat) {
    const key = `category:${cat.id}`;
    const isCollapsed = !!collapsed[cat.id] && !isFiltering;
    const unread =
      isCollapsed &&
      cat.children.some((c) => {
        if (c.type !== 'text') return false;
        const rs = channelReadStateById && channelReadStateById[c.channel.id];
        return !!rs && toInt(rs.unread_count, 0) > 0 && toInt(rs.show_notifications, 1) === 1;
      });
    return (
      <div key={key} role="group" aria-label={cat.name}>
        <button type="button" className={`vxo-cat-head${dropClass(key, 'into')}${dropClass(key)}`} aria-expanded={!isCollapsed} onClick={() => toggleCollapsed(cat.id)} {...arrangeProps(key, 'into')}>
          <span aria-hidden="true">{isCollapsed ? '▸' : '▾'}</span> {cat.name}
          {unread ? <span className="vxo-cat-unread" aria-label="Unread messages" /> : null}
        </button>
        {isCollapsed
          ? // Collapsed: the open channel and occupied voice channels stay visible (voice occupancy), like Discord.
            cat.children.map((c) => (c.type === 'divider' ? null : c.type === 'voice' ? renderChannel(c, { membersOnly: true }) : activeChannelId === c.channel.id ? renderChannel(c) : null))
          : cat.children.map((c) => (c.type === 'divider' ? renderDivider(c, `divider:${c.id}`) : renderChannel(c)))}
      </div>
    );
  }

  const addChoices = [
    ['text', 'Text channel', () => onCreateChannel('text')],
    ['voice', 'Voice channel', () => onCreateChannel('voice')],
    ...(onAddLayoutItem
      ? [
          ['category', 'Category', () => onAddLayoutItem('category')],
          ['divider', 'Divider', () => onAddLayoutItem('divider')],
        ]
      : []),
  ];

  // A list that starts with a labelled divider (e.g. the "Text" heading of a converted server) shows it in the
  // heading row, next to the buttons, exactly where the old TEXT heading was; otherwise the row says CHANNELS.
  const lead = !isFiltering && tree[0] && tree[0].type === 'divider' && tree[0].name ? tree[0] : null;
  const rest = lead ? tree.slice(1) : tree;

  return (
    <>
      <VoiceAnimationStyles />

      <div className="coms-sidebar-section" ref={rootRef}>
        <div className="coms-sidebar-section-title-row">
          {lead ? renderDivider(lead, `divider:${lead.id}`, { inline: true }) : <div className="coms-sidebar-section-title">CHANNELS</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, position: 'relative' }}>
            {onOrganise ? (
              <button type="button" className="coms-mini-btn tiny" title="Organise channels (order, categories, dividers, archive)" aria-label="Organise channels" onClick={onOrganise}>
                ⋯
              </button>
            ) : null}
            {isOwner ? (
              <button type="button" className="coms-mini-btn tiny" title="Add a channel, category or divider" aria-label="Add" aria-haspopup="menu" aria-expanded={addOpen} onClick={() => setAddOpen((v) => !v)} data-testid="sidebar-add">
                +
              </button>
            ) : null}
            {addOpen ? (
              <div className="vxo-add-menu" role="menu" onKeyDown={(e) => e.key === 'Escape' && setAddOpen(false)}>
                {addChoices.map(([k, label, fn]) => (
                  <button
                    key={k}
                    type="button"
                    role="menuitem"
                    data-testid={`sidebar-add-${k}`}
                    onClick={() => {
                      setAddOpen(false);
                      fn();
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        {rest.map((it) => (it.type === 'category' ? renderCategory(it) : it.type === 'divider' ? renderDivider(it, `divider:${it.id}`) : renderChannel(it)))}

        {canArrange ? (
          <div
            className={`vxo-drop-end${dropTarget === 'end:' ? ' is-drop-target' : ''}`}
            aria-hidden="true"
            onDragOver={(e) => {
              if (!dragKey.current) return;
              e.preventDefault();
              if (dropTarget !== 'end:') setDropTarget('end:');
            }}
            onDrop={(e) => {
              if (!dragKey.current) return;
              e.preventDefault();
              commit(moveItem(tree, dragKey.current, { end: true }));
            }}
          />
        ) : null}

        {archivedChannels && archivedChannels.length ? (
          <div role="group" aria-label="Archived channels">
            <button type="button" className="vxo-cat-head" aria-expanded={showArchived} onClick={() => setShowArchived((v) => !v)} data-testid="sidebar-archived-toggle">
              <span aria-hidden="true">{showArchived ? '▾' : '▸'}</span> Archived ({archivedChannels.length})
            </button>
            {showArchived
              ? archivedChannels.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={`coms-chan is-archived ${activeChannelId === c.id ? 'active' : ''}`}
                    data-channel-id={c.id}
                    title="Archived (read-only)"
                    onClick={() => onSelectChannel(c.id)}
                    onContextMenu={(e) => onChannelContextMenu(e, c)}
                    style={{ display: 'flex', alignItems: 'center', gap: 7 }}
                  >
                    <span aria-hidden="true">🗄️</span>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}># {c.name}</span>
                  </button>
                ))
              : null}
          </div>
        ) : null}
      </div>
    </>
  );
}

function toInt(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.floor(n) : fallback;
}
